import React, { useState, useRef } from "react";
import { Typography, Box, Divider } from "@mui/material";
import Div from "@jumbo/shared/Div";
import VideoItem from "components/VideoItem";
import { HEIGHT_OFFSET } from "app/utils/constants/constants";
import getEmbedUrl from "app/utils/embedUrl";
import getVideoThumbnail from "components/getVideoThumbnail";
import LoadingView from "components/LoadingView";
import PricingCards from "components/PricingCards";

const VideosTabWrapper = ({ component: Component = Box, sx, children }) => (
  <Component sx={{ ...sx }}>{children}</Component>
);

const VideosTabNoDataPlaceholder = ({ children }) => (
  <Div
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minWidth: 0,
      height: `calc(100vh - ${HEIGHT_OFFSET.DEFAULT})`,
    }}
  >
    {children}
  </Div>
);

const VideosTab = ({ isLoading, videos }) => {
  const [loadedVideos, setLoadedVideos] = useState({});
  const [activeVideo, setActiveVideo] = useState(null);
  const videoPlayerRef = useRef(null);

  const handlePlayVideo = (video) => {
    setActiveVideo(video);
    if (videoPlayerRef.current) {
      videoPlayerRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const activeVideos =
    (videos?.videos &&
      videos?.videos?.filter((video) => video.active === true)) ||
    [];
  const isPremiumCustomer = videos?.premiumCustomer === true;

  if (isLoading) {
    return (
      <VideosTabWrapper>
        <LoadingView message="Loading videos..." />
      </VideosTabWrapper>
    );
  }

  return (
    <VideosTabWrapper
      sx={{
        height: `calc(100vh - ${HEIGHT_OFFSET.DEFAULT})`,
        overflowY: "auto",
        overflowX: "hidden",
        width: "100%",
        padding: 2,
      }}
    >
      {/* Video Player Section */}
      <Div ref={videoPlayerRef}>
        {activeVideo && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {activeVideo.videoTitle || "Untitled Video"}
            </Typography>
            <div style={{ padding: "56.25% 0 0 0", position: "relative" }}>
              <iframe
                src={`${getEmbedUrl(
                  activeVideo.videoLink,
                  activeVideo.provider
                )}?badge=0&autopause=0&player_id=0&app_id=58479`}
                frameborder="0"
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                }}
                title={
                  activeVideo.videoTitle || `${activeVideo.provider} Video`
                }
              ></iframe>
            </div>
            <script src="https://player.vimeo.com/api/player.js"></script>
            {activeVideo.videoDescription && (
              <Typography variant="body2" sx={{ mt: 2, color: "#8595A6" }}>
                {activeVideo.videoDescription}
              </Typography>
            )}
          </Box>
        )}
      </Div>

      {activeVideos.length === 0 && !isLoading && isPremiumCustomer && (
        <VideosTabNoDataPlaceholder>
          <Typography variant="h6" sx={{ textAlign: "center" }}>
            Hold on! We are crafting mind blowing video tutorials to level up
            your skills. <br />
            Watch this space!
          </Typography>
        </VideosTabNoDataPlaceholder>
      )}
      {activeVideos.length > 0 && (
        <Div>
          {activeVideos.map((video, index) => (
            <React.Fragment key={index}>
              <VideoItem
                video={video}
                index={index}
                sectionIndex={0}
                loadedVideos={loadedVideos}
                setLoadedVideos={setLoadedVideos}
                handlePlayVideo={handlePlayVideo}
                getVideoThumbnail={getVideoThumbnail}
                getEmbedUrl={getEmbedUrl}
              />
              {index < activeVideos.length - 1 && <Divider sx={{ my: 1 }} />}
            </React.Fragment>
          ))}
        </Div>
      )}

      {!isPremiumCustomer && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="h6" sx={{ mb: 2, textAlign: "center" }}>
            Unlock Premium Content with a Subscription
          </Typography>

          <PricingCards size="small" />
        </Box>
      )}
    </VideosTabWrapper>
  );
};

export default VideosTab;
